<template>
    <v-container>
        <entity-filter
            class="mb-n1"
            :isLoading="isLoading"
            :useEvents="useEvents"
            :teams="teams"
            :hideStatus="isArchive"
            :eventsValues="eventsValues"
            :eventsLabel="eventsLabel"
            :select-all-events="selectAllEvents"
            @change="filterChange"
        ></entity-filter>
        <slot
            name="currentList"
            v-bind:entities="filteredEntities"
            v-bind:searchTerm="searchTerm"
            v-bind:readRoute="readRoute"
        >
            <v-data-table
                :headers="headers"
                :items="filteredEntities"
                :items-per-page="15"
                dense
                class="elevation-1"
                :search="searchTerm"
                :footer-props="{
                    showFirstLastPage: true,
                    itemsPerPageOptions: [-1, 10, 15, 20, 25],
                    firstIcon: 'mdi-arrow-collapse-left',
                    lastIcon: 'mdi-arrow-collapse-right',
                    prevIcon: 'mdi-minus',
                    nextIcon: 'mdi-plus',
                }"
            >
                <template v-slot:[`item.name`]="{ item }">
                    <router-link :to="{ name: readRoute, params: { id: item.keyId } }" :title="item.name">
                        {{ item.name ? item.name : '[NO NAME]' }}
                    </router-link>
                </template>
                <template v-slot:[`item.archiveDate`]="{ item }">
                    {{ item.archiveDate | luxonSortableDateTime }}
                </template>
            </v-data-table>
        </slot>
    </v-container>
</template>
<script>
import api from '@/api'
import { mapState, mapGetters } from 'vuex'
import { uniqBy } from 'lodash'
import entities from './entities'
import permissionsSet from './permissions'
import EntityFilter from './EntityFilter.vue'

export default {
    components: {
        'entity-filter': EntityFilter,
    },
    props: {
        entity: {
            type: String,
            required: true,
            validator: value => {
                return entities.includes(value)
            },
        },
        apiName: {
            type: String,
            required: false,
        },
        useEvents: {
            type: Boolean,
            required: true,
        },
        eventsLabel: {
            type: String,
            required: false,
            default: 'Events',
        },
        eventsValues: {
            type: Array,
            required: false,
            default: () => [],
        },
        selectAllEvents: {
            type: Boolean,
            required: false,
            default: false,
        },
        newRouteName: {
            type: String,
            required: false,
        },
        readRouteName: {
            type: String,
            required: false,
        },
        entityHeaders: {
            type: Array,
            required: false,
        },
        entityArchiveHeaders: {
            type: Array,
            required: false,
        },
        entitySort: {
            type: Function,
            required: false,
            default: undefined,
        },
        filterOnShare: {
            type: Boolean,
            required: false,
            deafult: false,
        },
        teamId: {
            type: [String, Number],
            required: false,
        },
        isArchive: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isLoading: false,
            entities: [],
            selectedStatus: null,
            teams: [],
            selectedTeam: 0,
            searchTerm: null,
            selectedEvent: 0,
        }
    },
    computed: {
        ...mapState(['user']),
        ...mapGetters(['events', 'nextEvent', 'currentEvent']),
        apiEntityAddress() {
            return this.apiName || `${this.entity}s`
        },
        filteredEntities() {
            if (this.entityFilter) {
                return this.entityFilter(this.entities, this.selectedTeam, this.selectedStatus)
            }
            let entities = this.entities
            if (this.selectedStatus != null) {
                entities = entities.filter(x => x.statusId == this.selectedStatus)
            }
            if (this.selectedTeam > 0) {
                entities = entities.filter(
                    x =>
                        x.teamId == this.selectedTeam ||
                        (this.filterOnShare ? x.sharedTeams.map(x => x.id).includes(this.selectedTeam) : false),
                )
            }
            return entities
        },
        userDefaultTeamId() {
            if (this.user && this.user.defaultTeam) {
                let teamId = parseInt(this.user.defaultTeam)
                if (!isNaN(teamId)) return teamId
            }
            return -1
        },
        capsEnt() {
            return this.entity[0].toUpperCase() + this.entity.substring(1)
        },
        newRoute() {
            return this.newRouteName ? this.newRouteName : `new${this.capsEnt}`
        },
        readRoute() {
            return this.readRouteName ? this.readRouteName : `read${this.capsEnt}`
        },
        headers() {
            const headers = [
                {
                    text: 'Name',
                    align: 'start',
                    value: 'name',
                    width: '50%',
                },
                { text: 'Team', value: 'teamName', filterable: false, width: '25%' },
                { text: 'Status', value: 'statusText', filterable: false, width: '25%' },
            ]
            return this.entityHeaders && this.entityHeaders.length > 0 ? this.entityHeaders : headers
        },
        archiveHeaders() {
            const headers = [
                {
                    text: 'Name',
                    align: 'start',
                    value: 'name',
                    width: '50%',
                },
                { text: 'Team', value: 'teamName', filterable: false, width: '25%' },
                { text: 'Archive Date', value: 'createdDate', filterable: false, width: '25%' },
            ]
            return this.entityArchiveHeaders && this.entityArchiveHeaders.length > 0
                ? this.entityArchiveHeaders
                : headers
        },
        canCreate() {
            if (this.user && this.user[this.entity]) {
                return this.user[this.entity].owned.includes(permissionsSet.Create)
            } else {
                return false
            }
        },
        event() {
            if (!this.selectedEvent || !this.events || this.events.length === 0) return
            let event = this.events.find(x => x.id === this.selectedEvent)
            return event
        },
    },
    watch: {
        selectedEvent() {
            if (this.useEvents) this.getEntities()
        },
        currentEvent() {
            if (!this.eventsValues || this.eventsValues.length === 0) {
                this.selectedEvent = this.currentEvent
            }
        },
    },
    activated() {
        // Instead of onMounted as this component lives in a keep-alive tag with a slightly different lifecycle
        if (this.currentEvent) {
            if (this.selectedEvent === this.currentEvent && this.useEvents) {
                this.getEntities()
            } else {
                this.selectedEvent = this.currentEvent
            }
        }
        let teamIdAsInt = parseInt(this.teamId)
        if (!isNaN(teamIdAsInt)) {
            this.selectedTeam = teamIdAsInt
        }
        if (!this.useEvents) this.getEntities()
    },
    methods: {
        getEntities() {
            if (this.isArchive) {
                this.getArchivedEntities()
                return
            }
            if (this.useEvents && this.selectedEvent === 0) return
            this.isLoading = true
            let withAllFilter = this.selectAllEvents && this.selectedEvent === 'ALL'
            let apiUrl =
                this.useEvents && !withAllFilter
                    ? `/${this.apiEntityAddress}?eventId=${this.selectedEvent}`
                    : `/${this.apiEntityAddress}`
            api.get(apiUrl)
                .then(res => {
                    if (this.entitySort) {
                        this.entities = this.entitySort(res)
                    } else {
                        this.entities = this.orderByUserTeams(res)
                    }
                    this.teams = this.getAllTeam().concat(
                        uniqBy(res, 'teamName').map(x => {
                            return { value: x.teamId, text: x.teamName }
                        }),
                    )
                })
                .finally(() => (this.isLoading = false))
        },
        getArchivedEntities() {
            this.isLoading = true
            let withAllFilter = this.filterAll && this.selectedEvent === 'ALL'
            let apiUrl =
                this.useEvents && !withAllFilter
                    ? `/${this.apiEntityAddress}/archive?eventId=${this.selectedEvent}`
                    : `/${this.apiEntityAddress}/archive`
            api.get(apiUrl)
                .then(res => {
                    if (this.entitySort) {
                        this.entities = this.entitySort(res)
                    } else {
                        this.entities = this.orderByUserTeams(res)
                    }
                    this.teams = this.getAllTeam().concat(
                        uniqBy(res, 'teamName').map(x => {
                            return { value: x.teamId, text: x.teamName }
                        }),
                    )
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        getAllTeam() {
            return [{ value: 0, text: 'All' }]
        },
        orderByUserTeams(entityArray) {
            if (this.userDefaultTeamId === -1) return entityArray
            var orderedentities = entityArray.sort((a, b) => {
                let ownedA = a.teamId === this.userDefaultTeamId
                let ownedB = b.teamId === this.userDefaultTeamId
                if (ownedA === ownedB) {
                    var nameA = a.name.toUpperCase()
                    var nameB = b.name.toUpperCase()
                    if (nameA < nameB) {
                        return -1
                    }
                    if (nameA > nameB) {
                        return 1
                    }

                    // names must be equal??
                    return 0
                }
                return ownedA ? -1 : 1
            })
            return orderedentities
        },
        filterChange(filterState) {
            this.selectedStatus = filterState.selectedStatus
            this.selectedTeam = filterState.selectedTeam
            this.searchTerm = filterState.searchTerm
            this.selectedEvent = filterState.selectedEvent
        },
    },
}
</script>
